import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost, secureGetPdf } from "../../config/api";
import { useHistory, useLocation } from "react-router-dom";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { PrimaryButton } from "../../components/PrimaryButton";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";

import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg";
import DownloadIcon from "../../icons/downloadB.svg";
import EditIcon from "../../icons/edit.svg";

const BILLING_INVOICE_STATUS_CHIP_COLORS = {
  DRAFT: "#A8ADC3",
  ISSUED: "#0283F4",
  PAID: "#06D6A0",
  CANCELLED: "#FEA26F",
  OVERDUE: "#EF476F",
};

const getBillingInvoiceStatusChipColor = (status) => {
  const key = String(status).toUpperCase();
  return BILLING_INVOICE_STATUS_CHIP_COLORS[key] ?? "#EF476F";
};

const Invoices = () => {
  const [, setInvoicesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateRange: {},
    status: [],
    needle: "",
    grayclientId: null,
  });
  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const location = useLocation();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);


  useEffect(() => {
    if (location.state?.showSnackbar) {
      console.log("Snackbar message:", location.state.snackbarMessage);
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;


  const columns = [
    { label: t(`columns.invoiceNumber`), key: "invoiceNumber" },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    {
      label: t(`columns.periodEnd`), key: "periodEnd",
      render: (date) => formatDateTime(date)
    },
    {
      label: t(`columns.issuedAt`), key: "issuedAt",
      render: (date) => formatDateTime(date)
    },
    {
      label: t(`columns.dueDate`), key: "dueDate",
      render: (date) => formatDateTime(date)
    },
    { label: t(`columns.grayClient`), key: "grayClient", render: (client) => client?.companyName || "N/A" },
    { label: t(`columns.totalHT`), key: "priceHT" },
    { label: t(`columns.totalTTC`), key: "priceTTC" },
    {
      label: t(`columns.status`),
      key: "status",
      render: (status) => (
        <span
          className="inline-flex items-center justify-center rounded-[12px] px-2 py-1 text-xs font-semibold text-white"
          style={{ backgroundColor: getBillingInvoiceStatusChipColor(status) }}
        >
          {status != null ? t(`billingInvoiceStatus.${status}`) : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const status = (item?.status ?? "").toString().toUpperCase();
    const isPaidOrCancelled = status === "PAID" || status === "CANCELLED" || status === "ISSUED";
    const isExtraSeatInvoice = item?.extraSeatsInvoice ?? false;
    const options = [
      { label: t(`actions.details`), icon: EyeIcon, action: () => history.push({ pathname: `/invoices/details/${item.id}`, state: { filters, page: currentPage, size: pageSize } }) },
      { label: t(`actions.downloadInvoice`), icon: DownloadIcon, action: () => handleDownload(item) }
    ];
    if (!isPaidOrCancelled && !isExtraSeatInvoice) {
      options.push(
        { label: t(`actions.modify`) || "Modifier", icon: EditIcon, action: () => history.push({ pathname: `/invoices/modify/${item.id}`, state: { filters, page: currentPage, size: pageSize } }) },
      );
    }
    return options;
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "grayclientId",
      label: t(`columns.grayClient`),
      type: "dropdown",
      options: [...clients.map((client) => ({
        value: client.id,
        label: client.companyName,
      }))],
    },
    {
      name: "status",
      label: t(`columns.status`),
      type: "dropdown",
      options: [
        { value: "DRAFT", label: t(`billingInvoiceStatus.DRAFT`) },
        { value: "ISSUED", label: t(`billingInvoiceStatus.ISSUED`) },
        { value: "PAID", label: t(`billingInvoiceStatus.PAID`) },
        { value: "OVERDUE", label: t(`billingInvoiceStatus.OVERDUE`) },
        { value: "CANCELLED", label: t(`billingInvoiceStatus.CANCELLED`) },
      ],
    },
  ];

  const fetchGrayClients = useCallback(async () => {
    try {
      const response = await secureGet("/grayClients", showSnackbar, { signal });
      setClients(response.data.content);
    } catch (error) {
      console.error("Error fetching grayClients:", error);
    }
  }, [signal]);

  useEffect(() => {
    fetchGrayClients();
  }, [fetchGrayClients]);

  const fetchPaginatedInvoices = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("size", size);
      if (activeFilters.needle) params.append("needle", activeFilters.needle);
      if (activeFilters.startDate) params.append("startDate", activeFilters.startDate);
      if (activeFilters.endDate) params.append("endDate", activeFilters.endDate);
      if (activeFilters.status) params.append("status", activeFilters.status);
      if (activeFilters.grayclientId) params.append("grayClientId", activeFilters.grayclientId);

      const response = await secureGet(`/billing-invoices?${params.toString()}`, showSnackbar, {
        signal,
      });

      const { content, totalPages } = response.data;
      setInvoicesData(content);
      console.log("Invoices data:", content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching invoices:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedInvoices(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedInvoices(0, pageSize, filters);
    }
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedInvoices(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else if (filterName === "status") {
      updatedFilters.status = filterValue;
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: {},
      status: [],
      needle: "",
      grayclientId: null,
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedInvoices(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await secureDelete(`/billing-invoices/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedInvoices(currentPage, pageSize);
      showSnackbar(t(`confirmation.invoiceDelete`), 'success');
    } catch (error) {
      console.error("Error deleting invoice:", error);
      setDeletePopupVisible(false);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/invoices/details/${item.id}`,
      state: {
        filters,
        page: currentPage,
        size: pageSize,
      },
    });
  };


  const handleDownload = async (item) => {
    try {
      const response = await secureGetPdf(`/billing-invoices/${item.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${item.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download invoice:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.invoices")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.invoices`)}</h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => handleDetails(row)}
        />
      </TableLoadingArea>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Invoices;